import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  Scrape, 
  Company, 
  fetchScrapes, 
  fetchCompanies, 
  extractCategories, 
  updatePricingPageUrl,
  updateCompanyScraper,
  bulkUpdateScraper,
  updateNoPricingFlag,
  updateScrapeVerified
} from '@/helpers/scrapeDatabase';
import {
  runSingleScrape,
  runBulkScrapes,
  verifyScreenshots,
  rerunFailedTasks,
  showTaskResultToast,
  getTodayVersion
} from '@/helpers/taskManager';
import {
  sortScrapes,
  filterScrapes,
  getStatusCounts,
  getVerificationCounts,
  getInvalidCount,
  getNoPricingCount,
  formatTimestamp
} from '@/helpers/scrapeUtils';
import { ScrapesFilters } from '@/components/admin/ScrapesFilters';
import { BulkActions } from '@/components/admin/BulkActions';
import { ScrapesTable } from '@/components/admin/ScrapesTable';
import { RunScrapeModals } from '@/components/admin/RunScrapeModals';
import { ResultAlerts } from '@/components/admin/ResultAlerts';
import { PricingUrlModal } from '@/components/admin/PricingUrlModal';

export const AdminScrapesTabRefactored: React.FC = () => {
  // Data state
  const [scrapes, setScrapes] = useState<Scrape[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [isRunning, setIsRunning] = useState(false);
  const [isRunningAllPages, setIsRunningAllPages] = useState(false);
  const [isRunningCategoryPages, setIsRunningCategoryPages] = useState(false);
  const [isBulkRerunning, setIsBulkRerunning] = useState(false);
  const [isBulkUpdatingScraper, setIsBulkUpdatingScraper] = useState(false);
  const [isBulkVerifying, setIsBulkVerifying] = useState(false);
  const [verifyingScrapesIds, setVerifyingScrapesIds] = useState<string[]>([]);
  const [rerunningScrapesIds, setRerunningScrapesIds] = useState<string[]>([]);
  const [updatingScraperIds, setUpdatingScraperIds] = useState<string[]>([]);
  const [updatingPricingUrlIds, setUpdatingPricingUrlIds] = useState<string[]>([]);
  const [updatingNoPricingIds, setUpdatingNoPricingIds] = useState<string[]>([]);
  const [updatingVerifiedIds, setUpdatingVerifiedIds] = useState<string[]>([]);
  
  // UI state
  const [showErrorsOnly, setShowErrorsOnly] = useState(false);
  const [showInvalidOnly, setShowInvalidOnly] = useState(false);
  const [showUnverifiedOnly, setShowUnverifiedOnly] = useState(false);
  const [hideNoPricing, setHideNoPricing] = useState(true); // Default to true to hide no_pricing pages
  const [selectedScrapes, setSelectedScrapes] = useState<string[]>([]);
  const [sortColumn, setSortColumn] = useState('timestamp');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAllPagesModalOpen, setIsAllPagesModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [pricingUrlModalOpen, setPricingUrlModalOpen] = useState(false);
  
  // Form states
  const [selectedSlug, setSelectedSlug] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [forceFlag, setForceFlag] = useState(false);
  const [useAIFlag, setUseAIFlag] = useState(false);
  const [editingScrape, setEditingScrape] = useState<Scrape | null>(null);
  const [editingUrl, setEditingUrl] = useState('');
  
  // Result states
  const [allPagesResult, setAllPagesResult] = useState<{ success: boolean; message: string; count?: number } | null>(null);
  const [categoryPagesResult, setCategoryPagesResult] = useState<{ success: boolean; message: string; count?: number } | null>(null);

  // Load initial data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [scrapesData, companiesData] = await Promise.all([
        fetchScrapes(),
        fetchCompanies()
      ]);
      
      setScrapes(scrapesData);
      setCompanies(companiesData);
      setCategories(extractCategories(companiesData));
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort scrapes
  const filteredScrapes = filterScrapes(scrapes, showErrorsOnly, showInvalidOnly, showUnverifiedOnly, hideNoPricing);
  const sortedScrapes = sortScrapes(filteredScrapes, sortColumn, sortDirection);
  const statusCounts = getStatusCounts(scrapes);
  const invalidCount = getInvalidCount(scrapes);
  const verificationCounts = getVerificationCounts(scrapes);
  const noPricingCount = getNoPricingCount(scrapes);

  // Event handlers
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleScrapeSelection = (scrapeId: string, checked: boolean) => {
    setSelectedScrapes(prev => 
      checked 
        ? [...prev, scrapeId]
        : prev.filter(id => id !== scrapeId)
    );
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectedScrapes(checked ? sortedScrapes.map(s => s.slug) : []);
  };

  const handleRunScrape = async () => {
    if (!selectedSlug) return;
    
    setIsRunning(true);
    try {
      const result = await runSingleScrape(selectedSlug, forceFlag, useAIFlag);
      if (result.success) {
        toast.success(result.message || 'Scrape initiated successfully');
      } else {
        toast.error(result.message || 'Failed to initiate scrape');
      }
      setIsModalOpen(false);
      setSelectedSlug('');
    } catch (error) {
      console.error('Error running scrape:', error);
      toast.error('Failed to run scrape');
    } finally {
      setIsRunning(false);
    }
  };

  const handleRunAllPagesScrapes = async () => {
    setIsRunningAllPages(true);
    try {
      const result = await runBulkScrapes('all', forceFlag, useAIFlag);
      setAllPagesResult(result);
      if (result.success) {
        toast.success(result.message || 'All pages scrapes initiated successfully');
      } else {
        toast.error(result.message || 'Failed to initiate all pages scrapes');
      }
      setIsAllPagesModalOpen(false);
    } catch (error) {
      console.error('Error running all pages scrapes:', error);
      toast.error('Failed to run all pages scrapes');
    } finally {
      setIsRunningAllPages(false);
    }
  };

  const handleRunCategoryScrapes = async () => {
    if (!selectedCategory) return;
    
    setIsRunningCategoryPages(true);
    try {
      const result = await runBulkScrapes('category', forceFlag, useAIFlag, selectedCategory);
      setCategoryPagesResult(result);
      if (result.success) {
        toast.success(result.message || `Category '${selectedCategory}' scrapes initiated successfully`);
      } else {
        toast.error(result.message || 'Failed to initiate category scrapes');
      }
      setIsCategoryModalOpen(false);
      setSelectedCategory('');
    } catch (error) {
      console.error('Error running category scrapes:', error);
      setCategoryPagesResult({ success: false, message: 'Failed to initiate category scrapes' });
    } finally {
      setIsRunningCategoryPages(false);
    }
  };

  const handleVerifyScreenshot = async (scrape: Scrape) => {
    setVerifyingScrapesIds(prev => [...prev, scrape.slug]);
    try {
      await verifyScreenshots([scrape]);
      toast.success('Screenshot verification initiated');
      await loadData(); // Refresh data
    } catch (error) {
      console.error('Error verifying screenshot:', error);
      toast.error('Failed to verify screenshot');
    } finally {
      setVerifyingScrapesIds(prev => prev.filter(id => id !== scrape.slug));
    }
  };

  const handleVerifyAll = async () => {
    try {
      await verifyScreenshots(scrapes);
      toast.success(`Initiated verification for ${scrapes.length} screenshots`);
    } catch (error) {
      console.error('Error verifying all screenshots:', error);
      toast.error('Failed to verify screenshots');
    }
  };

  const handleRerunScrape = async (scrape: Scrape) => {
    setRerunningScrapesIds(prev => [...prev, scrape.slug]);
    try {
      const result = await rerunFailedTasks([scrape]);
      toast.success(`Rerun initiated: ${result.successCount} success, ${result.failureCount} failed`);
    } catch (error) {
      console.error('Error rerunning scrape:', error);
      toast.error('Failed to rerun scrape');
    } finally {
      setRerunningScrapesIds(prev => prev.filter(id => id !== scrape.slug));
    }
  };

  const handleBulkRerun = async () => {
    if (selectedScrapes.length === 0) return;
    
    setIsBulkRerunning(true);
    try {
      const selectedScrapesData = scrapes.filter(s => selectedScrapes.includes(s.slug));
      const result = await rerunFailedTasks(selectedScrapesData);
      
      toast.success(`Bulk rerun completed: ${result.successCount} success, ${result.failureCount} failed`);
      setSelectedScrapes([]);
    } catch (error) {
      console.error('Error bulk rerunning:', error);
      toast.error('Failed to rerun selected scrapes');
    } finally {
      setIsBulkRerunning(false);
    }
  };

  const handleBulkUpdateScraper = async (scraper: string) => {
    if (selectedScrapes.length === 0) return;
    
    setIsBulkUpdatingScraper(true);
    try {
      const selectedScrapesData = scrapes.filter(s => selectedScrapes.includes(s.slug));
      const companySlugs = selectedScrapesData.map(s => s.company_slug);
      const result = await bulkUpdateScraper(companySlugs, scraper);
      
      toast.success(`Bulk scraper update completed: ${result.successCount} success, ${result.failureCount} failed`);
      setSelectedScrapes([]);
      await loadData(); // Refresh data to show updated scrapers
    } catch (error) {
      console.error('Error bulk updating scraper:', error);
      toast.error('Failed to update scrapers');
    } finally {
      setIsBulkUpdatingScraper(false);
    }
  };

  const handleBulkVerify = async () => {
    if (selectedScrapes.length === 0) return;
    
    setIsBulkVerifying(true);
    try {
      const selectedScrapesData = scrapes.filter(s => selectedScrapes.includes(s.slug));
      await verifyScreenshots(selectedScrapesData);
      
      toast.success(`Initiated verification for ${selectedScrapes.length} selected screenshots`);
      setSelectedScrapes([]);
      await loadData(); // Refresh data to show updated verification status
    } catch (error) {
      console.error('Error bulk verifying screenshots:', error);
      toast.error('Failed to verify screenshots');
    } finally {
      setIsBulkVerifying(false);
    }
  };

  const handleScraperChange = async (scrape: Scrape, newScraper: string) => {
    setUpdatingScraperIds(prev => [...prev, scrape.slug]);
    try {
      await updateCompanyScraper(scrape.company_slug, newScraper);
      toast.success(`Updated scraper to ${newScraper}`);
      await loadData(); // Refresh data
    } catch (error) {
      console.error('Error updating scraper:', error);
      toast.error('Failed to update scraper');
    } finally {
      setUpdatingScraperIds(prev => prev.filter(id => id !== scrape.slug));
    }
  };

  const handleEditPricingUrl = (scrape: Scrape) => {
    setEditingScrape(scrape);
    setEditingUrl(scrape.pricing_page_url || '');
    setPricingUrlModalOpen(true);
  };

  const handleSavePricingUrl = async () => {
    if (!editingScrape) return;
    
    setUpdatingPricingUrlIds(prev => [...prev, editingScrape.slug]);
    try {
      await updatePricingPageUrl(editingScrape, editingUrl);
      toast.success('Pricing page URL updated successfully');
      setPricingUrlModalOpen(false);
      setEditingScrape(null);
      setEditingUrl('');
      await loadData(); // Refresh data
    } catch (error) {
      console.error('Error updating pricing URL:', error);
      toast.error('Failed to update pricing page URL');
    } finally {
      if (editingScrape) {
        setUpdatingPricingUrlIds(prev => prev.filter(id => id !== editingScrape.slug));
      }
    }
  };

  const handleClosePricingUrlModal = () => {
    setPricingUrlModalOpen(false);
    setEditingScrape(null);
    setEditingUrl('');
  };

  const handleNoPricingToggle = async (scrape: Scrape, noPricing: boolean) => {
    setUpdatingNoPricingIds(prev => [...prev, scrape.slug]);
    try {
      await updateNoPricingFlag(scrape, noPricing);
      toast.success(`No pricing flag ${noPricing ? 'enabled' : 'disabled'}`);
      await loadData(); // Refresh data
    } catch (error) {
      console.error('Error updating no pricing flag:', error);
      toast.error('Failed to update no pricing flag');
    } finally {
      setUpdatingNoPricingIds(prev => prev.filter(id => id !== scrape.slug));
    }
  };

  const handleVerifiedToggle = async (scrape: Scrape) => {
    setUpdatingVerifiedIds(prev => [...prev, scrape.slug]);
    try {
      // Always set to true when clicked (mark as valid)
      await updateScrapeVerified(scrape, true);
      toast.success('Scrape marked as verified');
      await loadData(); // Refresh data
    } catch (error) {
      console.error('Error updating verified status:', error);
      toast.error('Failed to update verified status');
    } finally {
      setUpdatingVerifiedIds(prev => prev.filter(id => id !== scrape.slug));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
        <div className="flex-1">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Scrapes Management</h2>
          <p className="text-sm text-gray-600">Manage and monitor your web scraping operations</p>
        </div>
        
        <div className="flex gap-2">
          <RunScrapeModals
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            selectedSlug={selectedSlug}
            setSelectedSlug={setSelectedSlug}
            companies={companies}
            forceFlag={forceFlag}
            setForceFlag={setForceFlag}
            useAIFlag={useAIFlag}
            setUseAIFlag={setUseAIFlag}
            isRunning={isRunning}
            onRunScrape={handleRunScrape}
            isAllPagesModalOpen={isAllPagesModalOpen}
            setIsAllPagesModalOpen={setIsAllPagesModalOpen}
            isRunningAllPages={isRunningAllPages}
            onRunAllPagesScrapes={handleRunAllPagesScrapes}
            isCategoryModalOpen={isCategoryModalOpen}
            setIsCategoryModalOpen={setIsCategoryModalOpen}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            categories={categories}
            isRunningCategoryPages={isRunningCategoryPages}
            onRunCategoryScrapes={handleRunCategoryScrapes}
          />
        </div>
      </div>

      {/* Filters Section */}
      <ScrapesFilters
        showErrorsOnly={showErrorsOnly}
        onShowErrorsOnlyChange={setShowErrorsOnly}
        showInvalidOnly={showInvalidOnly}
        onShowInvalidOnlyChange={setShowInvalidOnly}
        showUnverifiedOnly={showUnverifiedOnly}
        onShowUnverifiedOnlyChange={setShowUnverifiedOnly}
        hideNoPricing={hideNoPricing}
        onHideNoPricingChange={setHideNoPricing}
        errorCount={statusCounts.error || 0}
        invalidCount={invalidCount}
        unverifiedCount={verificationCounts.unverified}
        noPricingCount={noPricingCount}
        totalCount={scrapes.length}
        filteredCount={sortedScrapes.length}
      />
      
      {/* Bulk Actions Section */}
      <BulkActions
        selectedCount={selectedScrapes.length}
        isLoading={loading}
        isBulkRerunning={isBulkRerunning}
        isBulkUpdatingScraper={isBulkUpdatingScraper}
        isBulkVerifying={isBulkVerifying}
        onBulkRerun={handleBulkRerun}
        onBulkUpdateScraper={handleBulkUpdateScraper}
        onBulkVerify={handleBulkVerify}
        onVerifyAll={handleVerifyAll}
        onRefresh={loadData}
      />

      {/* Result alerts */}
      <ResultAlerts
        allPagesResult={allPagesResult}
        categoryPagesResult={categoryPagesResult}
        selectedCategory={selectedCategory}
      />

      {/* Main table */}
      <ScrapesTable
        scrapes={sortedScrapes}
        loading={loading}
        selectedScrapes={selectedScrapes}
        verifyingScrapesIds={verifyingScrapesIds}
        rerunningScrapesIds={rerunningScrapesIds}
        updatingScraperIds={updatingScraperIds}
        updatingPricingUrlIds={updatingPricingUrlIds}
        updatingNoPricingIds={updatingNoPricingIds}
        updatingVerifiedIds={updatingVerifiedIds}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={handleSort}
        onScrapeSelection={handleScrapeSelection}
        onSelectAll={handleSelectAll}
        onVerifyScreenshot={handleVerifyScreenshot}
        onRerunScrape={handleRerunScrape}
        onScraperChange={handleScraperChange}
        onEditPricingUrl={handleEditPricingUrl}
        onNoPricingToggle={handleNoPricingToggle}
        onVerifiedToggle={handleVerifiedToggle}
      />

      {/* Pricing URL Modal */}
      <PricingUrlModal
        isOpen={pricingUrlModalOpen}
        scrape={editingScrape}
        editingUrl={editingUrl}
        isUpdating={updatingPricingUrlIds.includes(editingScrape?.slug || '')}
        onClose={handleClosePricingUrlModal}
        onSave={handleSavePricingUrl}
        onUrlChange={setEditingUrl}
      />
    </div>
  );
};
